import { classSet } from '@platform/ui-library';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  CSSProperties,
  MutableRefObject,
  ReactNode,
  useEffect,
  useRef
} from 'react';
import styles from './VirtualScroll.module.scss';

export type VirtualScrollApi = {
  /**
   * @returns whether the scroll worked (false when trying to scroll past the bottom).
   */
  scrollToIndex: (
    index: number,
    options?: {
      align?: 'start' | 'center' | 'end' | 'auto';
      behavior?: ScrollBehavior;
    }
  ) => boolean;
};

export type VirtualScrollProps<T> = {
  /** Imperative handle for scrolling to a specific item. */
  apiRef?: MutableRefObject<VirtualScrollApi | null>;
  classNames?: {
    itemsContainer?: string;
    scrollContainer?: string;
  };
  estimateItemSize: number | ((item: T, index: number) => number);
  /** Element to include in the scrollable area after the rendered items. */
  footer: ReactNode;
  getItemKey?: (item: T, index: number) => string | number;
  /** Element to include in the scrollable area before the rendered items. */
  header: ReactNode;
  items: T[];
  onScrollIndexChange?: (index: number) => void;
  /** Amount of @see items above & below the viewport to render so the user never sees empty space. */
  overscan?: number;
  renderItem: (item: T, index: number) => ReactNode;
  styles?: {
    itemsContainer?: CSSProperties;
    scrollContainer?: CSSProperties;
  };
};

export function VirtualScroll<T>({
  apiRef,
  classNames,
  estimateItemSize,
  footer,
  getItemKey,
  header,
  items,
  onScrollIndexChange,
  overscan = 3,
  renderItem,
  styles: inlineStyles
}: VirtualScrollProps<T>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollIndexRef = useRef(0);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (typeof estimateItemSize !== 'function') ? (
      () => estimateItemSize
    ) : (
      (index) => estimateItemSize(items[index], index)
    ),
    getItemKey: (!getItemKey) ? undefined : (
      (index) => getItemKey(items[index], index)
    ),
    overscan
  });
  const scrollOffset = virtualizer.scrollOffset ?? 0;
  const visibleIndex = virtualizer.getVirtualItems().find(
    (item) => item.end > scrollOffset
  )?.index;

  useEffect(() => {
    if (!apiRef) {
      return;
    }
    apiRef.current = {
      scrollToIndex: (index, options) => {
        const scrollElement = scrollRef.current;
        const atBottom = (
          !!scrollElement
          && (
            scrollElement.scrollTop + scrollElement.clientHeight
          ) >= scrollElement.scrollHeight
        );
        if (atBottom && index > scrollIndexRef.current) {
          // Do not scroll to index past the end.
          return false;
        }
        scrollIndexRef.current = index;
        virtualizer.scrollToIndex(index, options);
        return true;
      }
    };
    return () => {
      apiRef.current = null;
    };
  }, [apiRef, virtualizer]);

  useEffect(() => {
    if (!onScrollIndexChange || visibleIndex == null) {
      return;
    }
    if (visibleIndex !== scrollIndexRef.current) {
      scrollIndexRef.current = visibleIndex;
      onScrollIndexChange(visibleIndex);
    }
  }, [onScrollIndexChange, scrollOffset, items.length, visibleIndex]);

  return (
    <div
      ref={scrollRef}
      className={classSet([styles.scrollContainer, classNames?.scrollContainer])}
      style={inlineStyles?.scrollContainer}
    >
      {header}
      <div
        className={classSet([styles.itemsContainer, classNames?.itemsContainer])}
        style={{ height: virtualizer.getTotalSize(), ...inlineStyles?.itemsContainer }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => (
          <div
            key={virtualItem.key as string | number}
            ref={virtualizer.measureElement}
            data-index={virtualItem.index}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItem.start}px)`
            }}
          >
            {renderItem(items[virtualItem.index], virtualItem.index)}
          </div>
        ))}
      </div>
      {footer}
    </div>
  );
}
